import React, {memo, useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useTypedSelector} from "../../../../redux/Hooks/storeSelectors";
import {selectMapLayerIdByEntityName} from "../../../../redux/selectors/selectors";
import {PublicTransitLayerEntityName} from "../../../../api/entities/replancity_PublicTransitLine";
import {isErrorResponse} from "../../../../utils/utils";
import {useToastContext} from "../../../../context/toastContext";
import Button from "../../../../components/buttons/Button";
import {useLayersInteractivity} from "../../../../hooks/map/useLayersInteractivity";
import {publicTransitApi} from "../../../../api/publicTransitApi";
import {useMap} from "react-map-gl";
import mapboxgl from "mapbox-gl";
import {getFeatureCenter, getUniqueFeatures} from "../../../../utils/mapUtils";
import {MapboxGeoJSONFeatureWithProperties} from "../../../../redux/map/types";
import {flewToPoint} from "../../../../redux/map/map-reducer";
import {useDispatch} from "react-redux";


type Props = {
    routeProfileId: string;
    onStopAdditionFn: (reloadLayer?: boolean) => Promise<void>;
}

const AddStopToRoute = memo(({routeProfileId, onStopAdditionFn}: Props) => {
    const entityName = PublicTransitLayerEntityName.STOPS;
    const routeProfileStopEntityName = PublicTransitLayerEntityName.ROUTE_STOPS;
    const stopsLayerId = useTypedSelector((state) => selectMapLayerIdByEntityName(state, entityName));
    const [selectionMode, setSelectionMode] = useState<boolean>(false);
    const isSavingRef = useRef(false);
    const {
        recoverLayersInteractivity,
        disableLayersInteractivityExceptEntityName,
        toggleRedirectOnFeatureSelection
    } = useLayersInteractivity();
    const {mapboxMapId} = useMap();
    const {t} = useTranslation();
    const {addToast} = useToastContext();
    const abortControllerRef = useRef(new AbortController());
    const dispatch = useDispatch();

    const keyPress = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            setSelectionMode(false);
            recoverLayersInteractivity();
            toggleRedirectOnFeatureSelection(true);
        }
    }

    useEffect(() => {
        document.addEventListener('keyup', keyPress);

        return () => {
            document.removeEventListener('keyup', keyPress);
            recoverLayersInteractivity();
            toggleRedirectOnFeatureSelection(true);
        }
    }, []);

    const addStop = useCallback(async (event: mapboxgl.MapLayerMouseEvent) => {
        if (isSavingRef.current) {
            return;
        }

        isSavingRef.current = true;

        const {target, originalEvent} = event;
        originalEvent.stopPropagation();

        const features = target.queryRenderedFeatures(event.point, {
            layers: [stopsLayerId]
        });
        const uniqueFeatures = getUniqueFeatures(features as MapboxGeoJSONFeatureWithProperties[]);

        if (uniqueFeatures.length > 1) {
            const [feature] = uniqueFeatures;
            const [lng, lat] = getFeatureCenter(feature);
            if (lng && lat) {
                addToast(t("transit-lines.stop-selection-several-stops-warn"), 'warning');
                dispatch(flewToPoint({lng, lat, zoom: 20}));
            }
            isSavingRef.current = false;

            return;
        }

        for (const feature of uniqueFeatures) {
            const resp = await publicTransitApi.addRouteStop(
                {
                    routeProfileStop: {
                        publicTransitRouteProfile: {
                            id: routeProfileId
                        },
                        transitStop: {
                            id: feature.properties.id
                        }
                    }
                },
                abortControllerRef.current.signal
            )

            if (isErrorResponse(resp as any)) {
                addToast(typeof resp.error === 'string' ? resp.error : resp.error.error, 'error');
            } else {
                addToast(`${t('common.save-successful')}`, 'success');

                //TODO onStopAdditionFn in hook dependencies recreates addStop and doesn't allow to _map?.off('click', stopsLayerId, addStop)
                await onStopAdditionFn(true);
            }
        }

        isSavingRef.current = false;
    }, [stopsLayerId, routeProfileStopEntityName, routeProfileId])

    const btnClickHandler = useCallback(async () => {
        setSelectionMode(prev => !prev);
    }, [setSelectionMode])

    useEffect(() => {
        if (selectionMode) {
            mapboxMapId?.on('click', stopsLayerId, addStop);

            disableLayersInteractivityExceptEntityName(entityName);
            toggleRedirectOnFeatureSelection(false);
        } else {
            mapboxMapId?.off('click', stopsLayerId, addStop);

            recoverLayersInteractivity();
            toggleRedirectOnFeatureSelection(true);
        }

        return () => {
            mapboxMapId?.off('click', stopsLayerId, addStop);
        }
    }, [entityName, selectionMode])

    return (
        <>
            <Button
                text={selectionMode ? t('transit-lines.stop-selection-mode-on') : t('transit-lines.stop-selection-mode-off')}
                onClick={btnClickHandler}
                colorType="transparent"
                testId="addStopToRouteBtn"
                disabled={isSavingRef.current}
            />
        </>
    )
})

export default AddStopToRoute;